import { cloneRgba, type Rgba } from '../color.ts';
import { clampElevation } from './document.ts';
import { DEFAULT_ROCK_COLOR } from './palette.ts';
import {
  clearRingEdgeBendById,
  findRingAtPoint,
  findRingBend,
  findRingById,
  findRingEdge,
  findRingVertex,
  makeRingVertices,
  moveRingBendById,
  moveRingVertexById,
  removeRingById,
  removeRingVertexById,
  setRingColorById,
  setRingCornerRadiusById,
  setRingEdgeBendById,
} from './ringObjects.ts';
import {
  DEFAULT_CORNER_RADIUS,
  DEFAULT_ROCK_ELEVATION,
  MIN_RING_VERTICES,
  type MapDocument,
  type Rock,
  type Vec2,
} from './types.ts';

export function addRock(map: MapDocument, vertices: readonly Vec2[]): Rock {
  if (vertices.length < MIN_RING_VERTICES) {
    throw new Error(`A rock needs at least ${MIN_RING_VERTICES} vertices`);
  }

  const rock: Rock = {
    id: `rock-${map.nextRockId}`,
    color: cloneRgba(DEFAULT_ROCK_COLOR),
    elevation: DEFAULT_ROCK_ELEVATION,
    vertices: makeRingVertices(map.bounds, vertices),
    edges: vertices.map(() => ({ bend: null })),
    cornerRadius: DEFAULT_CORNER_RADIUS,
  };

  map.nextRockId += 1;
  map.rocks.push(rock);
  return rock;
}

export function findRock(map: MapDocument, rockId: string): Rock | undefined {
  return findRingById(map.rocks, rockId);
}

export function findRockAt(map: MapDocument, point: Vec2): Rock | undefined {
  return findRingAtPoint(map.rocks, point);
}

export function findRockVertexAt(
  map: MapDocument,
  point: Vec2,
  radius: number,
): { rockId: string; index: number } | null {
  const found = findRingVertex(map.rocks, point, radius);
  return found ? { rockId: found.id, index: found.index } : null;
}

export function moveRockVertex(
  map: MapDocument,
  rockId: string,
  index: number,
  point: Vec2,
): boolean {
  return moveRingVertexById(map.rocks, map.bounds, rockId, index, point);
}

export function removeRockVertex(map: MapDocument, rockId: string, index: number): boolean {
  return removeRingVertexById(map.rocks, rockId, index);
}

export function removeRock(map: MapDocument, rockId: string): boolean {
  return removeRingById(map.rocks, rockId);
}

export function setRockCornerRadius(map: MapDocument, rockId: string, radius: number): boolean {
  return setRingCornerRadiusById(map.rocks, rockId, radius);
}

export function setRockColor(map: MapDocument, rockId: string, color: Rgba): boolean {
  return setRingColorById(map.rocks, rockId, color);
}

export function setRockElevation(map: MapDocument, rockId: string, elevation: number): boolean {
  const rock = findRock(map, rockId);

  if (!rock) {
    return false;
  }

  rock.elevation = clampElevation(elevation);
  return true;
}

export function findRockEdgeAt(
  map: MapDocument,
  rockId: string,
  point: Vec2,
  radius: number,
): number | null {
  return findRingEdge(map.rocks, rockId, point, radius);
}

export function findRockBendAt(
  map: MapDocument,
  rockId: string,
  point: Vec2,
  radius: number,
): number | null {
  return findRingBend(map.rocks, rockId, point, radius);
}

export function setRockEdgeBend(
  map: MapDocument,
  rockId: string,
  edgeIndex: number,
  point: Vec2,
): boolean {
  return setRingEdgeBendById(map.rocks, map.bounds, rockId, edgeIndex, point);
}

export function moveRockBend(
  map: MapDocument,
  rockId: string,
  edgeIndex: number,
  point: Vec2,
): boolean {
  return moveRingBendById(map.rocks, map.bounds, rockId, edgeIndex, point);
}

export function clearRockEdgeBend(map: MapDocument, rockId: string, edgeIndex: number): boolean {
  return clearRingEdgeBendById(map.rocks, rockId, edgeIndex);
}
